import { useContext, useEffect, useState } from 'react';
import { OrderProviderValue, ProductItemModel, ProductItemsModel, RaffleCountDownModel, StoreProviderValue, UserProviderValue } from '../models';
import { ProductItemTypes } from '../enums';
import { getRaffleCountdownText } from '../utils';
import { OrderContext } from '../contexts/order.context';
import { UserContext } from '../contexts/user.context';
import { StoreContext } from '../contexts/store.context';
import { useCountryContext } from '../contexts/country.context';
import { setSweepstakeTermsOfServicesRead } from '../services';
import { toast } from 'react-toastify';

export const useProductItem = (productItem: ProductItemModel) => {
   const { placeOrder, isLoading: isOrderLoading } = useContext<OrderProviderValue>(OrderContext);
   const { user, isAuthenticated } = useContext<UserProviderValue>(UserContext);
   const { raffles, setRaffles, userRaffleEntries, setUserRaffleEntries } = useContext<StoreProviderValue>(StoreContext);
   const { countries, isSweepstakeCountryAllowed } = useCountryContext();

   const [raffleCountdown, setRaffleCountdown] = useState<RaffleCountDownModel | null>(null);
   const [inspect, setInspect] = useState(false);
   const [showDetails, setShowDetails] = useState(false);
   const [addToCartQuantity, setAddToCartQuantity] = useState(0);
   const [isTosLoading, setIsTosLoading] = useState(false);

   const [tosAccepted, setTosAccepted] = useState(false);

   const isLoading = isOrderLoading || isTosLoading;

   useEffect(() => {
      if (productItem.type !== ProductItemTypes.RAFFLE) return;

      setRaffleCountdown(getRaffleCountdownText(productItem.startsAt ? new Date(productItem.startsAt) : null, productItem.endsAt ? new Date(productItem.endsAt) : null));

      setTosAccepted(productItem.userEntries > 0);

      const interval = setInterval(() => {
         setRaffleCountdown(getRaffleCountdownText(productItem.startsAt ? new Date(productItem.startsAt) : null, productItem.endsAt ? new Date(productItem.endsAt) : null));
      }, 10000);

      return () => clearInterval(interval);
   }, [productItem]);

   useEffect(() => {
      if (!inspect) {
         setShowDetails(false);
         return;
      }
   }, [inspect]);

   const acceptTos = async (): Promise<void> => {
      setTosAccepted(true);

      if (productItem.isPurchasable === false && productItem.userEntries === 0) {
         setIsTosLoading(true);
         try {
            const response = await setSweepstakeTermsOfServicesRead(productItem.id);

            if (response.error) {
               setTosAccepted(false);
               toast.error('Failed to accept terms and conditions');
               return;
            }

            if (response.data?.productItem) {
               const updatedItem = response.data.productItem as ProductItemModel;

               const updateProductItems = (items: ProductItemsModel) => ({
                  ...items,
                  productItems: items.productItems?.map((item) => (item.id === productItem.id ? updatedItem : item)) ?? null,
               });

               setRaffles(updateProductItems(raffles));
               if (userRaffleEntries?.productItems) {
                  setUserRaffleEntries(updateProductItems(userRaffleEntries));
               }
            }

            toast.success('Terms accepted! You earned a free entry.');
         } catch (error) {
            setTosAccepted(false);
            toast.error('Failed to accept terms and conditions');
         } finally {
            setIsTosLoading(false);
         }
      }
   };

   const confirmEntries = async (): Promise<any> => {
      if (!tosAccepted) return;

      // Validate country before purchase
      const hasValidCountry = user?.country && countries.find((c) => c.value === user.country);
      if (!hasValidCountry) {
         toast.error('Please select your country to purchase sweepstakes entries');
         return;
      }

      if (!isSweepstakeCountryAllowed(user.country)) {
         toast.error('Sweepstakes purchases are not available in your country');
         return;
      }

      await placeOrder(productItem, addToCartQuantity);
      setInspect(false);

      setAddToCartQuantity(0);
   };

   return {
      raffleCountdown,
      inspect,
      setInspect,
      showDetails,
      setShowDetails,
      confirmEntries,
      addToCartQuantity,
      setAddToCartQuantity,
      isLoading,
      isAuthenticated,
      tosAccepted,
      setTosAccepted: acceptTos,
   };
};
